/**
 * Currency utility functions for formatting prices based on display currency
 */
import { i18n } from '../i18n/useI18n';

/**
 * Round up to the nearest whole unit, but absorb floating-point noise so a
 * value like 15.000000000000002 (the typical artefact of EUR→USD→EUR
 * roundtrips) does not jump to the next integer. The 100×EPSILON tolerance
 * matches `roundDisplayAmountForCurrency` so both layers agree on what
 * counts as "exact".
 */
export const ceilWithEpsilon = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const rounded = Math.ceil(n - Number.EPSILON * 100);
  // Normalise -0 → +0 so callers don't have to care about the sign of zero.
  return rounded || 0;
};

/**
 * Format price for display based on selected currency
 * @param {number} usdPrice - Price in USD
 * @param {string} displayCurrency - Selected display currency ('USD' or 'UAH')
 * @param {Object} currencyRates - Currency rates object with usd rate
 * @param {string} region - Current region (optional, for region-specific logic)
 * @returns {string} Formatted price string
 */
export const formatDisplayPrice = (usdPrice, displayCurrency = 'USD', currencyRates = {}, region = null) => {
  // Check if currency rates are available
  if (!currencyRates || typeof currencyRates.usd !== 'number') {
    // Return error message instead of crash
    if (region === 'ua' && displayCurrency === 'UAH') {
      const uahSymbol = i18n.t('currency.uahSymbol', 'UAH');
      return `(rate unavailable) ${uahSymbol}`;
    }
    return `$${ceilWithEpsilon(usdPrice)}`;
  }

  // Only convert for Ukraine region when UAH is selected
  if (region === 'ua' && displayCurrency === 'UAH') {
    const uahPrice = usdPrice * currencyRates.usd;
    const uahSymbol = i18n.t('currency.uahSymbol', 'UAH');
    return `${ceilWithEpsilon(uahPrice)} ${uahSymbol}`;
  }

  // For Europe region, show EUR
  // NOTE: Backend returns EU prices already in EUR, so no conversion needed
  if (region === 'eu') {
    return `€${ceilWithEpsilon(usdPrice)}`;
  }

  // Default to USD display for UA, Asia, Other
  return `$${ceilWithEpsilon(usdPrice)}`;
};

/**
 * Get currency symbol based on display currency and region
 * @param {string} displayCurrency - Selected display currency ('USD' or 'UAH')
 * @param {string} region - Current region
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (displayCurrency = 'USD', region = null) => {
  if (region === 'ua' && displayCurrency === 'UAH') {
    return i18n.t('currency.uahSymbol', 'UAH');
  }

  // For Europe region, return EUR symbol
  if (region === 'eu') {
    return '€';
  }

  // Default to USD for UA, Asia, Other
  return '$';
};

/**
 * Convert USD amount to UAH
 * @param {number} usdAmount - Amount in USD
 * @param {Object} currencyRates - Currency rates object
 * @returns {number} Amount in UAH or null if rates unavailable
 */
export const convertUSDToUAH = (usdAmount, currencyRates = {}) => {
  if (!currencyRates || typeof currencyRates.usd !== 'number') {
    return null; // Signal that conversion is not possible
  }
  return usdAmount * currencyRates.usd;
};

/**
 * Convert source-currency amount to USD using NBU rates (UAH-pivot).
 * rates.usd = UAH per 1 USD, rates.eur = UAH per 1 EUR, rates.kzt = KZT per 1 USD.
 *
 * NOTE: no `.toFixed(2)` here on purpose. Rounding to 2 decimals at insert
 * time introduces a tiny error (e.g. €3 → $3.53 instead of $3.5297) that
 * `Math.ceil(... * 10) / 10` later amplifies into a visible 0.1-unit drift
 * on the reverse trip (€3 catalog → €3.1 in calculator). We keep the full
 * precision in state; rounding happens only at the render layer.
 */
export const convertToUSD = (amount, currency, rates) => {
  if (amount == null || !currency || currency === 'USD') return amount;
  if (currency === 'EUR' && rates?.eur && rates?.usd) return amount * (rates.eur / rates.usd);
  if (currency === 'UAH' && rates?.usd) return amount / rates.usd;
  if (currency === 'KZT' && rates?.kzt) return amount / rates.kzt;
  return amount;
};

/**
 * Convert displayCurrency-amount → USD. Used on input blur for fields where
 * the user types a number in their displayed currency.
 */
export const convertDisplayToUSD = (amount, displayCurrency, rates) => {
  if (amount == null || !displayCurrency || displayCurrency === 'USD') return amount;
  if (displayCurrency === 'EUR' && rates?.eur && rates?.usd) return amount * (rates.eur / rates.usd);
  if (displayCurrency === 'UAH' && rates?.usd) return amount / rates.usd;
  if (displayCurrency === 'KZT' && rates?.kzt) return amount / rates.kzt;
  return amount;
};

/**
 * Convert USD amount → displayCurrency. Used when rendering a USD-internal value
 * for the user, or pre-filling an input field.
 */
export const convertUSDToDisplay = (usdAmount, displayCurrency, rates) => {
  if (usdAmount == null || !displayCurrency || displayCurrency === 'USD') return usdAmount;
  if (displayCurrency === 'EUR' && rates?.eur && rates?.usd) return usdAmount * (rates.usd / rates.eur);
  if (displayCurrency === 'UAH' && rates?.usd) return usdAmount * rates.usd;
  if (displayCurrency === 'KZT' && rates?.kzt) return usdAmount * rates.kzt;
  return usdAmount;
};

/**
 * Plain currency-code → symbol mapper. Use for input adornments where region
 * is not relevant; for region-aware rendering use getCurrencySymbol().
 */
export const getCurrencySymbolByCode = (code) => {
  switch (code) {
    case 'EUR': return '€';
    case 'UAH': return '₴';
    case 'KZT': return '₸';
    case 'USD':
    default: return '$';
  }
};

/**
 * Resolve effective currency for Phone Numbers / ENIQ / VoiceBot fields.
 * displayCurrency in UserPreferencesContext is only a USD/UAH switch for UA;
 * everywhere else region dictates the currency.
 *
 *   eu                          -> EUR
 *   ua + displayCurrency = UAH  -> UAH
 *   anything else                -> USD
 *
 * Not a global rule — Voice Traffic in Asia, for example, uses KZT via NBK
 * and follows different logic.
 */
export const getEffectiveDisplayCurrency = (region, displayCurrency) => {
  if (region === 'eu') return 'EUR';
  if (region === 'ua' && displayCurrency === 'UAH') return 'UAH';
  return 'USD';
};

/**
 * Round display prices for editable/manual phone-number prices.
 * UAH/KZT are shown as whole units; USD/EUR keep one decimal place.
 */
export const roundDisplayAmountForCurrency = (amount, currency) => {
  const value = Number(amount || 0);
  if (!Number.isFinite(value)) return 0;
  if (currency === 'USD' || currency === 'EUR') {
    return ceilWithEpsilon(value * 10) / 10;
  }
  return ceilWithEpsilon(value);
};

export const formatRoundedDisplayAmount = (amount, currency) => {
  const rounded = roundDisplayAmountForCurrency(amount, currency);
  const maxFractionDigits = currency === 'USD' || currency === 'EUR' ? 1 : 0;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(rounded);
};

/**
 * Convert USD-internal one-time payments into the currency used by subscription
 * plan paymentAmount. EU plans are EUR internally; other regions stay USD and
 * are converted later by formatDisplayPrice.
 */
export const convertUSDToSubscriptionAmount = (usdAmount, region, rates) => {
  if (region === 'eu') {
    return convertUSDToDisplay(usdAmount, 'EUR', rates);
  }
  return usdAmount;
};

/**
 * Format a USD-internal one-time payment for display, bypassing the global
 * EU no-conversion behavior used for backend-priced EU products.
 */
export const formatUSDInternalDisplayPrice = (usdAmount, region, displayCurrency, rates) => {
  const effectiveCurrency = getEffectiveDisplayCurrency(region, displayCurrency);
  const display = convertUSDToDisplay(usdAmount, effectiveCurrency, rates);
  const symbol = getCurrencySymbolByCode(effectiveCurrency);
  const rounded = ceilWithEpsilon(Number(display || 0));
  return `${symbol}${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(rounded)}`;
};
